import { useCallback, useEffect, useMemo, useRef } from "react"

import { useTableResizeContext } from "./context"

const MIN_COLUMN_RESIZE_WIDTH = 40

export const useColumnWidth = ({ columnIndex }) => {
  const { columnWidths } = useTableResizeContext()
  const width = columnWidths[columnIndex]
  if (!width) return undefined

  const pixelWidth = `${width}px`
  return { width: pixelWidth, minWidth: pixelWidth, maxWidth: pixelWidth }
}

export const useInicializeTableResize = ({ columns }) => {
  const { columnWidths, setColumnWidths, setActiveResizeColumn, setResizedColumns } = useTableResizeContext()
  const dragStateRef = useRef(null)
  const columnsKey = useMemo(() => columns.map((_, index) => index).join('|'), [columns])

  const onStartResize = useCallback(event => {
    const dragState = dragStateRef.current
    if (!dragState) return

    const delta = event.clientX - dragState.startX
    const nextWidth = Math.max(dragState.minWidth, Math.round(dragState.startWidth + delta))

    setColumnWidths(widths => {
      if (widths[dragState.columnIndex] === nextWidth) return widths
      return { ...widths, [dragState.columnIndex]: nextWidth }
    })
  }, [setColumnWidths])

  const onStopResize = useCallback(() => {
    dragStateRef.current = null
    setActiveResizeColumn(null)
    document.body.style.removeProperty('cursor')
    document.body.style.removeProperty('user-select')
    window.removeEventListener('mousemove', onStartResize)
    window.removeEventListener('mouseup', onStopResize)
  }, [onStartResize, setActiveResizeColumn])

  const onResize = useCallback(({ columnIndex, event }) => {
    event.preventDefault()
    event.stopPropagation()

    const headerElement = event.currentTarget?.parentElement
    const measuredWidth = Math.ceil(headerElement?.getBoundingClientRect?.().width || 0)
    if (Object.keys(columnWidths).length === 0) {
      const cells = headerElement?.parentElement?.querySelectorAll?.('th') || []
      const initialWidths = {}
      cells.forEach((cell, cellIndex) => {
        initialWidths[cellIndex] = Math.max(MIN_COLUMN_RESIZE_WIDTH, Math.ceil(cell.getBoundingClientRect().width || 0))
      })
      setColumnWidths(initialWidths)
    }

    const startWidth = Math.max(MIN_COLUMN_RESIZE_WIDTH, columnWidths[columnIndex] || measuredWidth || 0)
    dragStateRef.current = {
      columnIndex,
      startX: event.clientX,
      startWidth,
      minWidth: MIN_COLUMN_RESIZE_WIDTH,
    }

    setActiveResizeColumn(columnIndex)
    setResizedColumns(current => current.includes(columnIndex) ? current : [...current, columnIndex])

    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', onStartResize)
    window.addEventListener('mouseup', onStopResize)
  }, [columnWidths, onStartResize, onStopResize, setActiveResizeColumn, setColumnWidths, setResizedColumns])

  useEffect(() => () => onStopResize(), [onStopResize])
  useEffect(() => {
    onStopResize()
    setColumnWidths({})
    setResizedColumns([])
  }, [columnsKey, onStopResize, setColumnWidths, setResizedColumns])

  return {
    hadResized: Object.keys(columnWidths).length > 0,
    onResize,
  }
}
